import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axiosInstance from "../../utils/axios";
import { useSubdivision } from "../../hooks/useSubdivision";
import TooltipIcon from "./TooltipIcon";
import TinyMCEEditor from "../QuillEditor";
import {
  MLS_LISTING_STATUS_OPTIONS,
  HOME_TYPES,
  UNIT_HOME_TYPES,
  COMPASS_OPTIONS,
  RCR_YES_NO_OPTIONS,
  PRIVATE_POOL_OPTIONS,
  COMMUNITY_POOL_OPTIONS,
  WATER_VIEW_OPTIONS,
  FURNITURE_OPTIONS,
  GARAGE_OPTIONS,
  PETS_OPTIONS,
  RENTAL_TYPE_OPTIONS,
  HOMESTEAD_OPTIONS,
  YES_NO_OPTIONS,
  STATUS_WARNING_TEXT,
  SYSTEM_DATE_STATUSES,
  STATUS_DATE_MAP,
  STATUS_DATE_LABELS,
} from "../../constants/propertyFields";
import {
  LISTING_STATUS_TOOLTIP,
  COUNTY_PROPERTY_ID_TOOLTIP,
  SHORT_ADDRESS_TOOLTIP,
} from "../../constants/propertyTooltips";

// Property dates are calendar days stored at UTC midnight ("2024-02-22T00:00:00.000Z").
// Read the day straight off the ISO string — parsing it into a Date and formatting with a
// local-zone formatter rolls every US viewer back to the previous day.
const formatUtcDay = (value) => {
  if (!value) return "";
  const iso = value instanceof Date ? value.toISOString() : String(value);
  const [y, m, d] = iso.slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${Number(m)}/${Number(d)}/${y}`;
};

const US_STATES = [
  { label: "Alabama", value: "AL" }, { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" }, { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" }, { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" }, { label: "Delaware", value: "DE" },
  { label: "District of Columbia", value: "DC" }, { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" }, { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" }, { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" }, { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" }, { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" }, { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" }, { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" }, { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" }, { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" }, { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" }, { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" }, { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" }, { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" }, { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" }, { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" }, { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" }, { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" }, { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" }, { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" }, { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" }, { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

// Reusable field rendering helpers
function SelectField({ label, name, value, onChange, options, tooltip, required }) {
  return (
    <div className="mt-2">
      <label className="block text-sm font-medium first-letter:uppercase">
        {label}:{required && <span className="text-red-500 ml-1">*</span>}
        {tooltip && <TooltipIcon content={tooltip} />}
      </label>
      <select
        name={name}
        value={value || ""}
        onChange={onChange}
        className="mt-1 p-2 block w-full border rounded-md"
      >
        <option value="">Select an option</option>
        {options.map((opt) => {
          const val = typeof opt === "object" ? opt.value : opt;
          const lbl = typeof opt === "object" ? opt.label : opt;
          return (
            <option key={val} value={val}>{lbl}</option>
          );
        })}
      </select>
    </div>
  );
}

function TextField({ label, name, value, onChange, tooltip, required, type = "text", placeholder, maxLength }) {
  return (
    <div className="mt-2">
      <label className="block text-sm font-medium first-letter:uppercase">
        {label}:{required && <span className="text-red-500 ml-1">*</span>}
        {tooltip && <TooltipIcon content={tooltip} />}
      </label>
      <input
        type={type}
        name={name}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 p-2 block w-full border rounded-md"
      />
    </div>
  );
}

/** Format a number with commas: 1234567 → "1,234,567" */
function formatWithCommas(val) {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val).replace(/,/g, "");
  if (str === "" || str === "-") return str;
  const parts = str.split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return parts.join(".");
}

/** Numeric input that displays commas but stores raw number */
function NumericField({ label, name, value, onChange, tooltip, required, placeholder, allowDecimal }) {
  const displayValue = formatWithCommas(value);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/,/g, "");
    // Allow empty, digits, optional leading minus, optional decimal
    if (allowDecimal) {
      if (raw !== "" && !/^-?\d*\.?\d*$/.test(raw)) return;
    } else {
      if (raw !== "" && !/^-?\d*$/.test(raw)) return;
    }
    onChange({ target: { name, value: raw } });
  };

  return (
    <div className="mt-2">
      <label className="block text-sm font-medium first-letter:uppercase">
        {label}:{required && <span className="text-red-500 ml-1">*</span>}
        {tooltip && <TooltipIcon content={tooltip} />}
      </label>
      <input
        type="text"
        inputMode="numeric"
        name={name}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        className="mt-1 p-2 block w-full border rounded-md"
      />
    </div>
  );
}

function TextArea({ label, name, value, onChange }) {
  return (
    <div className="mt-2">
      <label className="block text-sm font-medium first-letter:uppercase">{label}:</label>
      <textarea
        name={name}
        value={value || ""}
        onChange={onChange}
        rows={3}
        className="mt-1 p-2 block w-full border rounded-md"
      />
    </div>
  );
}

// Tags that carry content on their own, so a note built only from them is not empty.
const EMBEDDED_CONTENT_TAGS = /<\s*(img|table|hr|iframe|video|audio|embed|object)\b/i;

/** True when rich-text HTML carries neither text nor embedded content. */
const richTextIsEmpty = (html) => {
  if (!html) return true;
  const value = String(html);
  if (EMBEDDED_CONTENT_TAGS.test(value)) return false;
  return value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim() === "";
};

/**
 * Rich-text note field. The same value is authored in the admin panel's Property
 * Notes tab, so a plain textarea here would show the stored markup as raw tags.
 * Reports through the shared handleChange with a synthetic event.
 */
function RichTextArea({ label, name, value, onChange }) {
  return (
    <div className="mt-2">
      <label className="block text-sm font-medium first-letter:uppercase">{label}:</label>
      <div className="mt-1">
        <TinyMCEEditor
          value={value || ""}
          height={280}
          onChange={(html) =>
            // A cleared editor still emits "<p><br></p>" — send "" so the form's
            // "" -> null mapping clears the column instead of storing markup.
            onChange({
              target: { name, value: richTextIsEmpty(html) ? "" : html },
            })
          }
        />
      </div>
    </div>
  );
}

/**
 * PropertyFormFields renders all spec-compliant form fields.
 *
 * @param {Object} props
 * @param {Object} props.formData - Current form state
 * @param {Function} props.handleChange - onChange handler: (e) => void
 * @param {string} props.mode - Form mode: "add-sale", "add-rent", "edit-sale", "edit-rent", "edit-sold", "edit-rented"
 * @param {Array} props.errors - Validation errors
 */
export default function PropertyFormFields({ formData, handleChange, mode, errors = [], propertyId }) {
  const isRental = mode.includes("rent");
  const isSale = mode.includes("sale") || mode.includes("sold");
  const isSold = mode === "edit-sold";
  const isRented = mode === "edit-rented";
  const isAdd = mode.startsWith("add");
  const currentStatus = formData.cw_listing_status || "ACTIVE";
  const showUnit = UNIT_HOME_TYPES.includes(formData.home_type);
  const statusWarning = STATUS_WARNING_TEXT[currentStatus];
  const isSystemDate = SYSTEM_DATE_STATUSES.includes(currentStatus);
  const statusDateField = STATUS_DATE_MAP[currentStatus];
  const showAnomalyField = currentStatus === "SOLD" || currentStatus === "RENTED";

  // Fetch allowed property types from admin config
  const [allowedHomeTypes, setAllowedHomeTypes] = useState(HOME_TYPES);
  useEffect(() => {
    axiosInstance
      .get("/api/community/allowed-property-types")
      .then((res) => {
        const config = res.data;
        if (config?.allowedTypes?.length) {
          const allowed = new Set(config.allowedTypes.map((t) => t.toUpperCase()));
          setAllowedHomeTypes(
            HOME_TYPES.filter((ht) => allowed.has(ht.value.toUpperCase()))
          );
        }
      })
      .catch(() => { });
  }, []);

  // Fetch subdivisions for the dropdown
  const { subdivisions: allSubdivisions } = useSubdivision();
  const filteredSubdivisions = useMemo(() => {
    if (!allSubdivisions) return [];
    if (!formData.home_type) return allSubdivisions;
    return allSubdivisions.filter((s) => s.homeType === formData.home_type);
  }, [allSubdivisions, formData.home_type]);

  // Wrap home_type change to clear subdivision_id if it no longer matches
  const handleHomeTypeChange = (e) => {
    const newType = e.target.value;
    handleChange(e);
    if (formData.subdivision_id && allSubdivisions) {
      const currentSub = allSubdivisions.find((s) => String(s.id) === String(formData.subdivision_id));
      if (currentSub && currentSub.homeType !== newType) {
        handleChange({ target: { name: "subdivision_id", value: "" } });
      }
    }
  };

  // Async County Property ID duplicate check
  const [countyIdWarning, setCountyIdWarning] = useState("");
  const countyIdTimer = useRef(null);

  const checkCountyPropertyId = useCallback((value) => {
    if (countyIdTimer.current) clearTimeout(countyIdTimer.current);
    if (!value) { setCountyIdWarning(""); return; }
    countyIdTimer.current = setTimeout(async () => {
      try {
        const res = await axiosInstance.post("/api/properties/validate-county-property-id", {
          county_property_id: value,
          listing_type: formData.listing_type,
          cw_listing_status: formData.cw_listing_status,
          exclude_id: propertyId || undefined,
        });
        if (res.data?.warning || (res.data && !res.data.valid)) {
          setCountyIdWarning(res.data.message || "This County Property ID already has an active listing.");
        } else {
          setCountyIdWarning("");
        }
      } catch {
        // Silently ignore - backend will catch on submit
      }
    }, 600);
  }, [formData.listing_type, formData.cw_listing_status, propertyId]);

  useEffect(() => {
    return () => { if (countyIdTimer.current) clearTimeout(countyIdTimer.current); };
  }, []);

  return (
    <div className="space-y-4">
      {/* CAPITALIZATION REMINDER */}
      <p className="text-red-600 font-bold text-sm">
        When you are entering information on the forms always use proper capitalization. No exceptions.
        For example: "123 Main Street", NOT "123 main street".
      </p>

      {/* ===== RCR FIELDS (conditional on listing type + status) ===== */}
      {(() => {
        const listingType = formData.listing_type || "";
        const status = currentStatus;
        const isSaleType = listingType === "Residential Sale";
        const isRentalType = listingType === "Residential Rental";
        const showSeller = isSaleType && (status === "ACTIVE" || status === "SOLD");
        const showBuyer = isSaleType && status === "SOLD";
        const showLandlord = isRentalType && (status === "ACTIVE" || status === "RENTED");
        const showTenant = isRentalType && status === "RENTED";
        const showAnyRcr = showSeller || showBuyer || showLandlord || showTenant;

        return (
          <fieldset className="border border-gray-300 rounded-lg p-4">
            <legend className="text-sm font-semibold px-2">RCR information</legend>

            <p className="text-xs text-gray-500 mt-1">Select YES if applicable, otherwise NO.</p>

            {showAnyRcr && (
              <div className="grid grid-cols-2 gap-4 mt-3">
                {showSeller && <SelectField label="RCR seller" name="rcr_seller" value={formData.rcr_seller} onChange={handleChange} options={RCR_YES_NO_OPTIONS} required />}
                {showLandlord && <SelectField label="RCR landlord" name="rcr_landlord" value={formData.rcr_landlord} onChange={handleChange} options={RCR_YES_NO_OPTIONS} required />}
                {showBuyer && <SelectField label="RCR buyer" name="rcr_buyer" value={formData.rcr_buyer} onChange={handleChange} options={RCR_YES_NO_OPTIONS} required />}
                {showTenant && <SelectField label="RCR tenant" name="rcr_tenant" value={formData.rcr_tenant} onChange={handleChange} options={RCR_YES_NO_OPTIONS} required />}
              </div>
            )}
          </fieldset>
        );
      })()}

      {/* ===== LISTING STATUS ===== */}
      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Listing information</legend>

        <SelectField
          label="MLS listing status"
          name="mls_listing_status"
          value={formData.mls_listing_status}
          onChange={handleChange}
          options={MLS_LISTING_STATUS_OPTIONS}
        />

        <div className="mt-2">
          <label className="block text-sm font-medium">
            CW listing status:
            <TooltipIcon content={LISTING_STATUS_TOOLTIP} />
          </label>
          <input
            type="text"
            name="cw_listing_status"
            value={currentStatus}
            readOnly
            disabled
            className="mt-1 p-2 block w-full border rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
          />
          <p className="text-gray-500 text-xs mt-0.5">Auto-derived from MLS Listing Status</p>
          {statusWarning && (
            <p className="text-red-600 text-sm mt-1 font-medium">{statusWarning}</p>
          )}
        </div>

        {/* Status date field — hidden for edit-sold/edit-rented (date shown in pricing section instead) */}
        {statusDateField && !isSold && !isRented && (
          <div className="mt-2">
            <label className="block text-sm font-medium">
              {STATUS_DATE_LABELS[statusDateField] || `${currentStatus} date`}
              {!isSystemDate && ["PENDING", "OFF MARKET", "CANCELLED", "WITHDRAWN", "EXPIRED"].includes(currentStatus) && (
                <span className="text-gray-500 text-xs ml-1">(from MLS)</span>
              )}
              {isSystemDate && <span className="text-gray-500 text-xs ml-1">(auto-generated)</span>}
            </label>
            <input
              type="date"
              name={statusDateField}
              value={formData[statusDateField] ? (formData[statusDateField].toString().split("T")[0]) : ""}
              onChange={handleChange}
              disabled={isSystemDate}
              className={`mt-1 p-2 block w-full border rounded-md ${isSystemDate ? "bg-gray-100" : ""}`}
            />
          </div>
        )}

        {/* MLS # — typed on add (auto-generated when left blank), editable on edit */}
        <TextField
          label="MLS #"
          name="mls_number"
          value={formData.mls_number}
          onChange={handleChange}
          required={!isAdd}
          maxLength={50}
          placeholder={isAdd ? "Auto-generated if left blank" : undefined}
        />

        {/* Listing Entry Date (read-only, system generated) */}
        {!isAdd && formData.listing_entry_date && (
          <div className="mt-2">
            <label className="block text-sm font-medium">Listing entry date:</label>
            <input
              type="text"
              value={formatUtcDay(formData.listing_entry_date)}
              disabled
              className="mt-1 p-2 block w-full border rounded-md bg-gray-100"
            />
          </div>
        )}
      </fieldset>

      {/* ===== COUNTY PROPERTY ID ===== */}
      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Property identification</legend>

        <TextField
          label="County property ID"
          name="county_property_id"
          value={formData.county_property_id}
          onChange={(e) => {
            handleChange(e);
            checkCountyPropertyId(e.target.value);
          }}
          tooltip={COUNTY_PROPERTY_ID_TOOLTIP}
        />
        {countyIdWarning && (
          <p className="text-amber-600 text-sm mt-1 font-medium">{countyIdWarning}</p>
        )}

        <SelectField
          label="Home type"
          name="home_type"
          value={formData.home_type}
          onChange={handleHomeTypeChange}
          options={allowedHomeTypes}
          required
        />

        <SelectField
          label="Short subdivision name"
          name="subdivision_id"
          value={formData.subdivision_id}
          onChange={handleChange}
          options={filteredSubdivisions.map((s) => ({ value: s.id, label: `${s.shortName} (${s.homeType})` }))}
          required
        />
      </fieldset>

      {/* ===== ADDRESS FIELDS ===== */}
      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Address</legend>

        <div className="grid grid-cols-4 gap-4">
          <TextField
            label="Building no"
            name="street_number"
            value={formData.street_number}
            onChange={handleChange}
          />
          <SelectField
            label="Compass"
            name="compass_direction"
            value={formData.compass_direction}
            onChange={handleChange}
            options={COMPASS_OPTIONS}
          />
          <TextField
            label="Street name"
            name="street_name"
            value={formData.street_name}
            onChange={handleChange}
            required
          />
          <SelectField
            label="Post street direction"
            name="post_street_direction"
            value={formData.post_street_direction}
            onChange={handleChange}
            options={COMPASS_OPTIONS}
          />
        </div>

        {showUnit && (
          <TextField
            label="Unit #"
            name="unit_number"
            value={formData.unit_number}
            onChange={handleChange}
            placeholder="Enter the address or condo identifier if there is no apartment number"
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <TextField label="City" name="city" value={formData.city} onChange={handleChange} required />
          <SelectField label="State" name="state" value={formData.state} onChange={handleChange} options={US_STATES} required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <TextField label="Zip code" name="zip_code" value={formData.zip_code} onChange={handleChange} required maxLength={5} placeholder="5 digits" />
          <TextField label="County" name="county_name" value={formData.county_name} onChange={handleChange} required />
        </div>

        <TextField
          label="Short address"
          name="short_address"
          value={formData.short_address}
          onChange={handleChange}
          tooltip={SHORT_ADDRESS_TOOLTIP}
        />

        <TextField
          label="Very short address"
          name="very_short_address"
          value={formData.very_short_address}
          onChange={handleChange}
        />
      </fieldset>

      {/* ===== PROPERTY DETAILS ===== */}
      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Property details</legend>

        <div className="grid grid-cols-3 gap-4">
          <NumericField label="Bedrooms" name="bedrooms" value={formData.bedrooms} onChange={handleChange} />
          <NumericField label="Total bathrooms" name="bathrooms" value={formData.bathrooms} onChange={handleChange} />
          <NumericField label="Half bathrooms" name="half_bathrooms" value={formData.half_bathrooms} onChange={handleChange} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <NumericField label="SF under AC" name="sf_under_ac" value={formData.sf_under_ac} onChange={handleChange} />
          <NumericField label="Year built" name="year_built" value={formData.year_built} onChange={handleChange} />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <SelectField label="Private pool" name="private_pool" value={formData.private_pool} onChange={handleChange} options={PRIVATE_POOL_OPTIONS} />
          <SelectField label="Community pool" name="community_pool" value={formData.community_pool} onChange={handleChange} options={COMMUNITY_POOL_OPTIONS} />
        </div>

        <SelectField label="Garage" name="garage" value={formData.garage} onChange={handleChange} options={GARAGE_OPTIONS} />
        <SelectField label="Water view" name="water_view" value={formData.water_view} onChange={handleChange} options={WATER_VIEW_OPTIONS} />

        <SelectField label="Furniture" name="furniture" value={formData.furniture} onChange={handleChange} options={FURNITURE_OPTIONS} />

        <SelectField label="Pets" name="pets" value={formData.pets} onChange={handleChange} options={PETS_OPTIONS} />
        <SelectField label="Homestead" name="homestead" value={formData.homestead} onChange={handleChange} options={HOMESTEAD_OPTIONS} />
      </fieldset>

      {/* ===== PRICING - FOR SALE ===== */}
      {isSale && (
        <fieldset className="border border-gray-300 rounded-lg p-4">
          <legend className="text-sm font-semibold px-2">Sale pricing</legend>

          {(currentStatus === "ACTIVE" || isAdd) && (
            <div className="grid grid-cols-3 gap-4">
              <NumericField
                label="Asking price"
                name="price_or_rent"
                value={formData.price_or_rent}
                onChange={handleChange}
                placeholder="Enter without $ sign"
                required
              />
              <NumericField
                label="Original asking price"
                name="original_asking_price"
                value={formData.original_asking_price}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <NumericField
                label="Real estate tax"
                name="real_estate_tax"
                value={formData.real_estate_tax}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <NumericField
                label="County assessed value"
                name="county_assessed_value"
                value={formData.county_assessed_value}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <NumericField
                label="Previous sold price"
                name="previous_sold_price"
                value={formData.previous_sold_price}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <TextField
                label="Previous sold date"
                name="previous_sold_date"
                value={formData.previous_sold_date ? formData.previous_sold_date.toString().split("T")[0] : ""}
                onChange={handleChange}
                type="date"
              />
              <TextField
                label="DOM (computed)"
                name="dom"
                value={formData.dom != null && formData.dom !== "" ? String(formData.dom) : (formData.date_became_active ? String(Math.max(0, Math.floor((Date.now() - new Date(formData.date_became_active).getTime()) / 86400000))) : "—")}
                readOnly
              />
            </div>
          )}

          {(currentStatus === "SOLD" || isSold) && (
            <div className="grid grid-cols-3 gap-4">
              <NumericField
                label="Asking price"
                name="price_or_rent"
                value={formData.price_or_rent}
                onChange={handleChange}
                placeholder="Enter without $ sign"
                required
              />
              <NumericField
                label="Original asking price"
                name="original_asking_price"
                value={formData.original_asking_price}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <NumericField
                label={<>MLS sold price <span>(from MLS)</span></>}
                name="mls_sold_price"
                value={formData.mls_sold_price}
                onChange={handleChange}
                placeholder="Enter without $ sign"
                required
              />
              <TextField
                label="MLS sold date"
                name="date_became_sold"
                value={formData.date_became_sold ? formData.date_became_sold.toString().split("T")[0] : ""}
                onChange={handleChange}
                type="date"
                required
              />
              <NumericField
                label="Real estate tax"
                name="real_estate_tax"
                value={formData.real_estate_tax}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <NumericField
                label="County assessed value"
                name="county_assessed_value"
                value={formData.county_assessed_value}
                onChange={handleChange}
                placeholder="Enter without $ sign"
              />
              <TextField
                label="DOM (snapshot)"
                name="dom"
                value={formData.dom != null && formData.dom !== "" ? String(formData.dom) : "—"}
                readOnly
              />
            </div>
          )}
        </fieldset>
      )}

      {/* ===== PRICING - FOR RENT ===== */}
      {isRental && (
        <fieldset className="border border-gray-300 rounded-lg p-4">
          <legend className="text-sm font-semibold px-2">Rental pricing</legend>

          {(currentStatus === "ACTIVE" || isAdd) && (
            <>
              <NumericField
                label="Asking rent"
                name="price_or_rent"
                value={formData.price_or_rent}
                onChange={handleChange}
                placeholder="Enter without $ sign"
                required
              />

              <SelectField label="Rental type" name="rental_type" value={formData.rental_type} onChange={handleChange} options={RENTAL_TYPE_OPTIONS} />

              <div className="grid grid-cols-3 gap-4 mt-2">
                <NumericField
                  label="Original asking rent"
                  name="original_asking_rent"
                  value={formData.original_asking_rent}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Real estate tax"
                  name="real_estate_tax"
                  value={formData.real_estate_tax}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <TextField
                  label="DOM (computed)"
                  name="dom"
                  value={formData.dom != null && formData.dom !== "" ? String(formData.dom) : (formData.date_became_active ? String(Math.max(0, Math.floor((Date.now() - new Date(formData.date_became_active).getTime()) / 86400000))) : "—")}
                  readOnly
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <NumericField
                  label="Furnished annual"
                  name="furnished_annual"
                  value={formData.furnished_annual}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Furnished seasonal"
                  name="furnished_seasonal"
                  value={formData.furnished_seasonal}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Furnished off season"
                  name="furnished_off_season"
                  value={formData.furnished_off_season}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished annual"
                  name="unfurnished_annual"
                  value={formData.unfurnished_annual}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished seasonal"
                  name="unfurnished_seasonal"
                  value={formData.unfurnished_seasonal}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished off season"
                  name="unfurnished_off_season"
                  value={formData.unfurnished_off_season}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
              </div>
              <TextField
                label="Available date"
                name="available_date"
                value={formData.available_date ? formData.available_date.toString().split("T")[0] : ""}
                onChange={handleChange}
                type="date"
              />
            </>
          )}

          {(currentStatus === "RENTED" || isRented) && (
            <>
              <SelectField label="Rental type" name="rental_type" value={formData.rental_type} onChange={handleChange} options={RENTAL_TYPE_OPTIONS} />

              <div className="grid grid-cols-3 gap-4">
                <NumericField
                  label="Asking rent"
                  name="price_or_rent"
                  value={formData.price_or_rent}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                  required
                />
                <NumericField
                  label={<>Rented price <span>(from MLS)</span></>}
                  name="rented_price"
                  value={formData.rented_price}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                  required
                />
                <TextField
                  label={<>Rented date <span>(from MLS)</span></>}
                  name="date_became_rented"
                  value={formData.date_became_rented ? formData.date_became_rented.toString().split("T")[0] : ""}
                  onChange={handleChange}
                  type="date"
                  required
                />
                <NumericField
                  label="Original asking rent"
                  name="original_asking_rent"
                  value={formData.original_asking_rent}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Real estate tax"
                  name="real_estate_tax"
                  value={formData.real_estate_tax}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <TextField
                  label="DOM (snapshot)"
                  name="dom"
                  value={formData.dom != null && formData.dom !== "" ? String(formData.dom) : "—"}
                  readOnly
                />
              </div>

              <div className="grid grid-cols-3 gap-4 mt-2">
                <NumericField
                  label="Furnished annual"
                  name="furnished_annual"
                  value={formData.furnished_annual}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Furnished seasonal"
                  name="furnished_seasonal"
                  value={formData.furnished_seasonal}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Furnished off season"
                  name="furnished_off_season"
                  value={formData.furnished_off_season}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished annual"
                  name="unfurnished_annual"
                  value={formData.unfurnished_annual}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished seasonal"
                  name="unfurnished_seasonal"
                  value={formData.unfurnished_seasonal}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
                <NumericField
                  label="Unfurnished off season"
                  name="unfurnished_off_season"
                  value={formData.unfurnished_off_season}
                  onChange={handleChange}
                  placeholder="Enter without $ sign"
                />
              </div>
            </>
          )}
        </fieldset>
      )}

      {/* ===== LINKS ===== */}
      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Links & notes</legend>

        <TextField label="MLS link" name="mls_link" value={formData.mls_link} onChange={handleChange} />
        <TextField label="County property link" name="county_property_link" value={formData.county_property_link} onChange={handleChange} />
        <RichTextArea label="CW private notes" name="cw_private_notes" value={formData.cw_private_notes} onChange={handleChange} />
        <TextArea label="CW public notes" name="cw_public_notes" value={formData.cw_public_notes} onChange={handleChange} />
      </fieldset>

      <fieldset className="border border-gray-300 rounded-lg p-4">
        <legend className="text-sm font-semibold px-2">Data quality</legend>
        <p className="text-red-600 font-bold text-sm">
          Set the following field to "YES" if we ever deviate from the information in the MLS or County records.
        </p>
        <SelectField
          label="Deviation from original data"
          name="deviation_from_original_data"
          value={formData.deviation_from_original_data}
          onChange={handleChange}
          options={YES_NO_OPTIONS}
          required
        />
        {showAnomalyField && (
          <>
            <p className="text-red-600 font-bold text-sm mt-3">
              Set the following field to "YES" if the transaction is an anomaly (outside the reasonable range of values and should not be used in statistical data or graphs. Examples: Unusually high sold price, unusually low rented price).
            </p>
            <SelectField
              label="Anomaly"
              name="anomaly"
              value={formData.anomaly}
              onChange={handleChange}
              options={YES_NO_OPTIONS}
              required
            />
          </>
        )}
      </fieldset>

      {/* ===== STATUS DATE HISTORY (edit mode) ===== */}
      {!isAdd && (
        <fieldset className="border border-gray-300 rounded-lg p-4 bg-gray-50">
          <legend className="text-sm font-semibold px-2">Status date history</legend>
          {/* Last Edited By */}
          <div className="text-sm text-gray-500">
            {formData.last_edited_by_name && (
              <div>
                <span className="font-medium">Last edited by:</span>{" "}
                <span className="text-gray-900">{formData.last_edited_by_name}</span>
                {formData.updatedAt && (
                  <span> on {new Date(formData.updatedAt).toLocaleString()} EST</span>
                )}
              </div>
            )}
            {!formData.last_edited_by_name && formData.updatedAt && (
              <div>
                <span className="font-medium">Last updated:</span>{" "}
                <span className="text-gray-900">{new Date(formData.updatedAt).toLocaleString()} EST</span>
              </div>
            )}
            {formData.last_status_changed_by_name && (
              <div className="mt-1">
                <span className="font-medium">Last status change by:</span>{" "}
                <span className="text-gray-900">{formData.last_status_changed_by_name}</span>
                {formData.last_status_changed_at && (
                  <span> on {new Date(formData.last_status_changed_at).toLocaleString()} EST</span>
                )}
              </div>
            )}
          </div>
        </fieldset>
      )}

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3">
          <p className="text-red-700 font-medium text-sm">Please fix the following errors:</p>
          <ul className="list-disc list-inside text-red-600 text-sm mt-1">
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
